import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Spinner } from "./spinner"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-[var(--c-bg)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-[var(--c-accent)] text-black hover:bg-[var(--c-accent-hover)] border border-[var(--c-accent)]",
        destructive:
          "bg-[var(--c-error)] text-white hover:bg-[var(--c-error)]/90 border border-[var(--c-error)]",
        outline:
          "border border-[var(--c-border)] bg-transparent hover:bg-[var(--c-surface-hover)] hover:text-[var(--c-text-primary)] text-[var(--c-text-secondary)]",
        secondary:
          "bg-[var(--c-surface)] text-[var(--c-text-primary)] hover:bg-[var(--c-surface-hover)] border border-[var(--c-border)]",
        ghost: "hover:bg-[var(--c-surface-hover)] hover:text-[var(--c-text-primary)] text-[var(--c-text-secondary)]",
        link: "text-[var(--c-accent)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * When `true`, the button will display a spinner and be disabled.
   */
  isLoading?: boolean
  /**
   * Where to place the spinner when `isLoading` is true. Defaults to `start` for
   * icon-only buttons (`size === "icon"`) and `end` for the rest.
   */
  spinnerPlacement?: "start" | "end"
  /**
   * Optional visually-hidden loading text for screen readers. When omitted the
   * button `children` will remain visible while the spinner is shown.
   */
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      spinnerPlacement,
      loadingText,
      children,
      disabled,
      ...rest
    },
    ref
  ) => {
    // Determine spinner position default
    const _spinnerPlacement = spinnerPlacement
      ? spinnerPlacement
      : size === "icon"
      ? "start"
      : "end"

    const isDisabled = disabled || isLoading

    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...rest}
        >
          {children}
        </Slot>
      )
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={isLoading}
        {...rest}
      >
        {/* Spinner at start position */}
        {isLoading && _spinnerPlacement === "start" && (
          <Spinner className={cn(size === "icon" ? "" : "mr-2", "h-4 w-4")} />
        )}

        {/* Text / children */}
        {loadingText ? (
          <span className={isLoading ? "opacity-0" : undefined}>{children}</span>
        ) : (
          children
        )}

        {/* Loading text (visually hidden) */}
        {isLoading && loadingText && (
          <span className={cn(size === "icon" ? "sr-only" : "ml-2")}>{loadingText}</span>
        )}

        {/* Spinner at end position */}
        {isLoading && _spinnerPlacement === "end" && (
          <Spinner className={cn(size === "icon" ? "" : "ml-2", "h-4 w-4")} />
        )}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
